import { sanitizeUrl } from './common.html.sanitize-url.js';

const coreAttrs = {
  class: (acc, value) => acc.className = value,
  id: (acc, value) => acc.id = value,
  dir: (acc, value) => acc.dir = value,
  name: (acc, value) => acc.id = value,
  itemprop: (acc, value) => acc.itemProp = value,
  itemscope: (acc, value) => acc.itemScope = value === "true",
  itemtype: (acc, value) => acc.itemType = value,
  lang: (acc, value) => acc.lang = value,
  title: (acc, value) => acc.title = value
};
const linkAttrs = {
  download: (acc, value) => acc.download = value,
  href: (acc, value) => acc.href = sanitizeUrl(value),
  hreflang: (acc, value) => acc.hrefLang = value,
  rel: (acc, value) => acc.rel = value,
  target: (acc, value) => acc.target = value
};
const sizeAttrs = {
  height: (acc, value) => acc.height = value,
  width: (acc, value) => acc.width = value
};
const mediaAttrs = {
  autoplay: (acc, value) => acc.autoPlay = value === "true",
  controls: (acc, value) => acc.controls = value === "true",
  crossorigin: (acc, value) => {
    switch (value) {
      case "":
      case "anonymous":
      case "use-credentials":
        acc.crossOrigin = value;
    }
  },
  loop: (acc, value) => acc.loop = value === "true",
  muted: (acc, value) => acc.muted = value === "true",
  preload: (acc, value) => acc.preload = value,
  src: (acc, value) => acc.src = sanitizeUrl(value)
};
const allowedAttributeMap = Object.freeze({
  a: {
    ...coreAttrs,
    ...linkAttrs
  },
  abbr: coreAttrs,
  address: coreAttrs,
  area: {
    ...coreAttrs,
    ...linkAttrs,
    alt: (acc, value) => acc.alt = value,
    coords: (acc, value) => acc.coords = value
  },
  article: coreAttrs,
  aside: coreAttrs,
  audio: {
    ...coreAttrs,
    ...mediaAttrs,
    ...sizeAttrs
  },
  b: coreAttrs,
  bdi: coreAttrs,
  bdo: coreAttrs,
  blockquote: {
    ...coreAttrs,
    cite: (acc, value) => acc.cite = sanitizeUrl(value)
  },
  br: {},
  caption: coreAttrs,
  cite: coreAttrs,
  code: coreAttrs,
  col: {
    ...coreAttrs,
    span: (acc, value) => acc.span = parseInt(value, 10)
  },
  colgroup: {
    ...coreAttrs,
    span: (acc, value) => acc.span = parseInt(value, 10)
  },
  data: coreAttrs,
  dd: coreAttrs,
  del: {
    ...coreAttrs,
    cite: (acc, value) => acc.cite = sanitizeUrl(value),
    datetime: (acc, value) => acc.dateTime = value
  },
  details: {
    ...coreAttrs,
    open: (acc, value) => acc.open = value === "true"
  },
  dfn: coreAttrs,
  div: coreAttrs,
  dl: coreAttrs,
  dt: coreAttrs,
  em: coreAttrs,
  figcaption: coreAttrs,
  figure: coreAttrs,
  footer: coreAttrs,
  h1: coreAttrs,
  h2: coreAttrs,
  h3: coreAttrs,
  h4: coreAttrs,
  h5: coreAttrs,
  h6: coreAttrs,
  header: coreAttrs,
  hgroup: coreAttrs,
  hr: coreAttrs,
  i: coreAttrs,
  iframe: {
    ...coreAttrs,
    height: (acc, value) => acc.height = value,
    width: (acc, value) => acc.width = value,
    allow: (acc, value) => acc.allow = value,
    allowFullscreen: (acc, value) => acc.allowFullScreen = value === "true",
    allowTransparency: (acc, value) => acc.allowTransparency = value === "true",
    loading: (acc, value) => {
      switch (value) {
        case "eager":
        case "lazy":
          acc.loading = value;
      }
    },
    referrerPolicy: (acc, value) => {
      switch (value) {
        case "":
        case "no-referrer":
        case "no-referrer-when-downgrade":
        case "origin":
        case "origin-when-cross-origin":
        case "same-origin":
        case "strict-origin":
        case "strict-origin-when-cross-origin":
        case "unsafe-url":
          acc.referrerPolicy = value;
      }
    },
    sandbox: (acc, value) => acc.sandbox = value,
    seamless: (acc, value) => acc.seamless = value === "true",
    src: (acc, value) => acc.src = sanitizeUrl(value)
  },
  img: {
    ...coreAttrs,
    ...sizeAttrs,
    alt: (acc, value) => acc.alt = value,
    crossorigin: (acc, value) => {
      switch (value) {
        case "":
        case "anonymous":
        case "use-credentials":
          acc.crossOrigin = value;
      }
    },
    sizes: (acc, value) => acc.sizes = value,
    src: (acc, value) => acc.src = value,
    srcset: (acc, value) => acc.srcSet = value,
    usemap: (acc, value) => acc.useMap = value
  },
  ins: {
    ...coreAttrs,
    cite: (acc, value) => acc.cite = sanitizeUrl(value),
    datetime: (acc, value) => acc.dateTime = value
  },
  li: {
    ...coreAttrs,
    value: (acc, value) => acc.value = value
  },
  map: {
    ...coreAttrs,
    name: (acc, value) => acc.name = value
  },
  mark: coreAttrs,
  nav: coreAttrs,
  ol: {
    ...coreAttrs,
    reversed: (acc, value) => acc.reversed = value === "true",
    start: (acc, value) => acc.start = parseInt(value, 10)
  },
  output: {
    ...coreAttrs,
    for: (acc, value) => acc.htmlFor = value,
    form: (acc, value) => acc.form = value,
    name: (acc, value) => acc.name = value
  },
  p: coreAttrs,
  picture: coreAttrs,
  pre: coreAttrs,
  q: {
    ...coreAttrs,
    cite: (acc, value) => acc.cite = sanitizeUrl(value)
  },
  rp: coreAttrs,
  rt: coreAttrs,
  ruby: coreAttrs,
  s: coreAttrs,
  samp: coreAttrs,
  section: coreAttrs,
  small: coreAttrs,
  source: {
    ...coreAttrs,
    media: (acc, value) => acc.media = value,
    sizes: (acc, value) => acc.sizes = value,
    src: (acc, value) => acc.src = sanitizeUrl(value),
    type: (acc, value) => acc.type = value
  },
  span: coreAttrs,
  strong: coreAttrs,
  sub: coreAttrs,
  summary: coreAttrs,
  sup: coreAttrs,
  table: {
    ...coreAttrs,
    summary: (acc, value) => acc.summary = value
  },
  tbody: coreAttrs,
  td: {
    ...coreAttrs,
    colspan: (acc, value) => acc.colSpan = parseInt(value, 10),
    headers: (acc, value) => acc.headers = value,
    rowspan: (acc, value) => acc.rowSpan = parseInt(value, 10)
  },
  tfoot: coreAttrs,
  th: {
    ...coreAttrs,
    colspan: (acc, value) => acc.colSpan = parseInt(value, 10),
    headers: (acc, value) => acc.headers = value,
    rowspan: (acc, value) => acc.rowSpan = parseInt(value, 10),
    scope: (acc, value) => acc.scope = value
  },
  thead: coreAttrs,
  time: {
    ...coreAttrs,
    datetime: (acc, value) => acc.dateTime = value
  },
  tr: coreAttrs,
  track: {
    ...coreAttrs,
    default: (acc, value) => acc.default = value === "true",
    kind: (acc, value) => acc.kind = value,
    label: (acc, value) => acc.label = value,
    src: (acc, value) => acc.src = sanitizeUrl(value),
    srclang: (acc, value) => acc.srcLang = value
  },
  u: coreAttrs,
  ul: coreAttrs,
  var: coreAttrs,
  video: {
    ...coreAttrs,
    ...mediaAttrs,
    height: (acc, value) => acc.height = value,
    width: (acc, value) => acc.width = value,
    poster: (acc, value) => acc.poster = sanitizeUrl(value)
  },
  wbr: {}
});

export { allowedAttributeMap, coreAttrs, linkAttrs };
//# sourceMappingURL=common.html.attribute-map.js.map
